/**
 * form-store.ts
 * Where field forms saved with no signal wait on the phone
 * (operator-offline-forms S6): one IndexedDB object store keyed by the
 * form's clientRequestId — the idempotency key the backend honors, so a send
 * whose answer was lost never creates a second record. Same posture as
 * dictation-store.ts: plain IndexedDB, its own database.
 *
 * `FormStore` is the seam form-outbox.ts talks to, so its tests run against
 * an in-memory store (jsdom has no IndexedDB).
 */
import type { RequestAbsence } from '@suite/contracts-rrhh';
import type {
  FleetFailureCreate,
  FleetFailureReportCreate,
  FuelLoadCreate,
  VehicleTripCreate,
} from '@suite/contracts-servicios';

export type FormState = 'waiting' | 'failed';

/** What each form sends, without the key and capture time the outbox adds. */
export type QueuedFormPayload =
  | { readonly kind: 'fuel-load'; readonly body: Omit<FuelLoadCreate, 'clientRequestId' | 'capturedAt'> }
  | { readonly kind: 'trip'; readonly body: Omit<VehicleTripCreate, 'clientRequestId' | 'capturedAt'> }
  | { readonly kind: 'absence'; readonly body: Omit<RequestAbsence, 'clientRequestId'> }
  | {
      readonly kind: 'failure';
      /** The informe is its own create, with its own key: report then failure, chained. */
      readonly reportClientRequestId: string;
      readonly report: Omit<FleetFailureReportCreate, 'clientRequestId' | 'capturedAt'>;
      readonly failure: Omit<FleetFailureCreate, 'clientRequestId' | 'reportId'>;
    };

export type QueuedFormKind = QueuedFormPayload['kind'];

export interface QueuedForm {
  readonly clientRequestId: string;
  /** `${tenantId}:${sub}` of whoever filled it; a shared phone never mixes users. */
  readonly owner: string;
  /** When it was filled on the phone (ISO): the business time the server records (S2). */
  readonly capturedAt: string;
  /** One line the worker recognizes it by on Hoy ("80 L · AB123CD"). */
  readonly summary: string;
  readonly payload: QueuedFormPayload;
  readonly state: FormState;
  /** Why the server rejected it (state 'failed'). */
  readonly error?: string;
}

export interface FormStore {
  all(): Promise<QueuedForm[]>;
  put(row: QueuedForm): Promise<void>;
  remove(clientRequestId: string): Promise<void>;
}

const DB_NAME = 'rigx-offline-forms';
const DB_VERSION = 1;
const FORMS = 'forms';

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(FORMS)) db.createObjectStore(FORMS, { keyPath: 'clientRequestId' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return promisify(run(db.transaction(FORMS, mode).objectStore(FORMS)));
}

export const indexedDbFormStore: FormStore = {
  all: () => withStore('readonly', (s) => s.getAll() as IDBRequest<QueuedForm[]>),
  put: async (row) => {
    await withStore('readwrite', (s) => s.put(row));
  },
  remove: async (clientRequestId) => {
    await withStore('readwrite', (s) => s.delete(clientRequestId));
  },
};
